import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService, GoAgent, GoAgentCommand, PatchReport } from '../services/api';
import { isLastSeenOnline, loadOnlineGrace } from '../utils/agentOnline';
import { Play, RefreshCw, Download, Trash2, MoreVertical, ShieldAlert, Scan, Database } from 'lucide-react';
import './GoAgentManagement.css';

const GoAgentManagement: React.FC = () => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<GoAgent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<GoAgent | null>(null);
  const [agentStatus, setAgentStatus] = useState<any>(null);
  const [commands, setCommands] = useState<GoAgentCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'agents' | 'patches' | 'history' | 'customScripts' | 'osquery'>('agents');
  const [patchReports, setPatchReports] = useState<PatchReport[] | null>(null);
  const [patchReportsLoading, setPatchReportsLoading] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [updateStatus, setUpdateStatus] = useState<{ [key: string]: string }>({});
  const [restarting, setRestarting] = useState<string | null>(null);
  const [restartStatus, setRestartStatus] = useState<{ [key: string]: string }>({});
  const [scanningDeviceId, setScanningDeviceId] = useState<string | null>(null);
  const [scanMessage, setScanMessage] = useState<{ deviceId: string; type: 'success' | 'error'; text: string } | null>(null);
  const [osquerySnapshots, setOsquerySnapshots] = useState<any[]>([]);
  const [osqueryLoading, setOsqueryLoading] = useState(false);
  
  // Command execution form state
  const [commandType, setCommandType] = useState<string>('powershell');
  const [commandText, setCommandText] = useState<string>('');
  const [timeoutSeconds, setTimeoutSeconds] = useState<number>(30);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<string | null>(null);

  // Load agents on mount
  useEffect(() => {
    loadOnlineGrace();
    loadAgents();
    // Refresh agents every 30 seconds
    const interval = setInterval(loadAgents, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load agent status when agent is selected
  useEffect(() => {
    if (selectedAgent) {
      loadAgentStatus();
      loadCommands();
      // Clear patch reports when switching agent (reload when opening Patches tab)
      setPatchReports(null);
      // Refresh status every 10 seconds
      const statusInterval = setInterval(() => {
        loadAgentStatus();
        loadCommands();
      }, 10000);
      return () => clearInterval(statusInterval);
    }
  }, [selectedAgent]);

  // Load patch reports when Patches tab is active and an agent is selected
  useEffect(() => {
    if (activeTab === 'patches' && selectedAgent && patchReports === null && !patchReportsLoading) {
      setPatchReportsLoading(true);
      apiService
        .getDevice(selectedAgent.device_id)
        .then((device) => {
          const reports = device?.device_metadata?.patch_reports;
          setPatchReports(Array.isArray(reports) ? reports : []);
        })
        .catch(() => setPatchReports([]))
        .finally(() => setPatchReportsLoading(false));
    }
  }, [activeTab, selectedAgent, patchReports, patchReportsLoading]);

  // Load Osquery snapshots when Osquery tab is active and an agent is selected
  useEffect(() => {
    if (activeTab === 'osquery' && selectedAgent) {
      setOsqueryLoading(true);
      apiService
        .getOsquerySnapshots(selectedAgent.device_id, 50)
        .then(({ snapshots }) => setOsquerySnapshots(snapshots))
        .catch(() => setOsquerySnapshots([]))
        .finally(() => setOsqueryLoading(false));
    }
  }, [activeTab, selectedAgent]);

  // Helper function to get OS category from OS string
  const getOSCategory = (os: string): string => {
    if (!os) return 'Unknown';
    const osLower = os.toLowerCase();
    if (osLower.includes('win')) return 'Windows';
    if (osLower.includes('linux') || osLower.includes('ubuntu') || osLower.includes('debian') || osLower.includes('centos') || osLower.includes('rhel') || osLower.includes('fedora')) return 'Linux';
    if (osLower.includes('mac') || osLower.includes('darwin') || osLower.includes('osx')) return 'macOS';
    if (osLower.includes('android')) return 'Android';
    if (osLower.includes('ios') || osLower.includes('iphone')) return 'iOS';
    return os; // Return original if no match
  };

  const loadAgents = async () => {
    try {
      setError(null);
      const response = await apiService.getGoAgents({ size: 100 });
      setAgents(response.items || []);
    } catch (err) {
      console.error('Failed to load agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const loadAgentStatus = async () => {
    if (!selectedAgent) return;
    try {
      const status = await apiService.getGoAgentStatus(selectedAgent.device_id);
      setAgentStatus(status);
    } catch (err) {
      console.error('Failed to load agent status:', err);
    }
  };

  const loadCommands = async () => {
    if (!selectedAgent) return;
    try {
      const response = await apiService.getGoAgentCommands(selectedAgent.device_id, { limit: 50 });
      setCommands(response.items || []);
    } catch (err) {
      console.error('Failed to load commands:', err);
    }
  };

  const handleExecuteCommand = async () => {
    if (!selectedAgent || !commandText.trim()) {
      setError('Please select an agent and enter a command');
      return;
    }

    setExecuting(true);
    setError(null);
    setExecutionResult(null);

    try {
      const result = await apiService.executeGoAgentCommand(selectedAgent.device_id, {
        command_type: commandType,
        command: commandText,
        timeout_seconds: timeoutSeconds,
        created_by: 'dashboard-user'
      });

      setExecutionResult(`Command submitted successfully! Command ID: ${result.command_id}`);
      
      // Clear form
      setCommandText('');
      
      // Refresh commands after a delay
      setTimeout(() => {
        loadCommands();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute command');
    } finally {
      setExecuting(false);
    }
  };

  const handleScanInventory = async (agent: GoAgent) => {
    setScanningDeviceId(agent.device_id);
    setScanMessage(null);
    try {
      await apiService.triggerDeviceInventorySync(agent.device_id, ['hardware', 'software']);
      setScanMessage({
        deviceId: agent.device_id,
        type: 'success',
        text: `Scan started for ${agent.name || agent.device_id}. Inventory will update shortly.`,
      });
    } catch (err) {
      setScanMessage({
        deviceId: agent.device_id,
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to trigger inventory scan',
      });
    } finally {
      setScanningDeviceId(null);
    }
  };

  const handleDeleteDevice = async (deviceId: string, deviceName: string) => {
    if (!window.confirm(`Are you sure you want to delete device "${deviceName}"? This will permanently delete the device and all associated data (commands, inventory, etc.).`)) {
      return;
    }

    setDeleting(deviceId);
    setError(null);
    setExecutionResult(null);

    try {
      const result = await apiService.deleteGoAgent(deviceId);
      setExecutionResult(result.message || `Device "${deviceName}" deleted successfully`);
      
      // Remove from agents list immediately
      setAgents(agents.filter(a => a.device_id !== deviceId));
      
      // Clear selection if deleted device was selected
      if (selectedAgent?.device_id === deviceId) {
        setSelectedAgent(null);
        setActiveTab('agents');
      }
      
      // Refresh agents list after a short delay
      setTimeout(() => {
        loadAgents();
      }, 1000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete device';
      setError(errorMessage);
      console.error('Delete device error:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleUpdateAgent = async (agent: GoAgent) => {
    if (!window.confirm(`Upgrade agent on "${agent.name || agent.device_id}"? The agent service will be stopped, updated, and restarted.`)) {
      return;
    }

    setUpdating(agent.device_id);
    setUpdateStatus(prev => ({ ...prev, [agent.device_id]: 'Fetching upgrade script...' }));
    setError(null);
    setExecutionResult(null);

    try {
      const os = agent.os?.toLowerCase() || '';
      const osFamily = os.includes('windows') ? 'windows' : 'linux';
      const updateCommand = await apiService.getUpgradeAgentScript(osFamily);
      const commandType = osFamily === 'windows' ? 'powershell' : 'bash';

      setUpdateStatus(prev => ({ ...prev, [agent.device_id]: 'Sending upgrade command...' }));

      const result = await apiService.executeGoAgentCommand(agent.device_id, {
        command_type: commandType,
        command: updateCommand,
        timeout_seconds: 300,
        created_by: 'dashboard-agent-upgrade'
      });

      setUpdateStatus(prev => ({ ...prev, [agent.device_id]: 'Upgrade command sent. Agent will update and restart.' }));
      setExecutionResult(`Upgrade command submitted. Command ID: ${result?.command_id}. The agent will download from the enrollment server (HTTP), replace the binary, and restart.`);

      const deviceId = agent.device_id;
      const pollInterval = setInterval(async () => {
        try {
          const commands = await apiService.getGoAgentCommands(deviceId, { limit: 10 });
          const cmd = commands.items?.find((c: any) => c.command_id === result?.command_id);
          if (cmd) {
            if (cmd.status === 'completed') {
              clearInterval(pollInterval);
              setUpdateStatus(prev => ({ ...prev, [deviceId]: 'Upgrade completed successfully!' }));
              setUpdating(prev => prev === deviceId ? null : prev);
              setTimeout(() => {
                loadAgents();
                if (selectedAgent?.device_id === deviceId) loadAgentStatus();
              }, 2000);
            } else if (cmd.status === 'failed') {
              clearInterval(pollInterval);
              setUpdateStatus(prev => ({ ...prev, [deviceId]: 'Upgrade failed!' }));
              setUpdating(prev => prev === deviceId ? null : prev);
              setError(`Upgrade failed: ${cmd.result?.output ?? cmd.error_message ?? 'Unknown error'}`);
            } else if (cmd.status === 'running') {
              setUpdateStatus(prev => ({ ...prev, [deviceId]: 'Upgrade in progress...' }));
            }
          }
        } catch (_) {}
      }, 5000);
      setTimeout(() => {
        clearInterval(pollInterval);
        setUpdating(prev => prev === deviceId ? null : prev);
      }, 300000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upgrade agent';
      setError(errorMessage);
      setUpdateStatus(prev => ({ ...prev, [agent.device_id]: `Error: ${errorMessage}` }));
      setUpdating(null);
    }
  };


  const handleRestartService = async (agent: GoAgent) => {
    if (!window.confirm(`Restart agent service on "${agent.name || agent.device_id}"?`)) {
      return;
    }

    setRestarting(agent.device_id);
    setRestartStatus(prev => ({ ...prev, [agent.device_id]: 'Restarting service...' }));
    setError(null);
    setExecutionResult(null);

    try {
      // Determine OS and build appropriate restart command
      const os = agent.os?.toLowerCase() || '';
      let restartCommand = '';
      let commandType = 'powershell';

      if (os.includes('windows')) {
        // Windows: Restart service using PowerShell
        restartCommand = `$ServiceName = "UEMAgent"; Write-Host "Restarting UEM Agent service..." -ForegroundColor Cyan; $service = Get-Service -Name $ServiceName -ErrorAction SilentlyContinue; if ($service) { if ($service.Status -eq "Running") { Write-Host "Stopping service..." -ForegroundColor Yellow; Stop-Service -Name $ServiceName -Force; Start-Sleep -Seconds 3 }; Write-Host "Starting service..." -ForegroundColor Yellow; Start-Service -Name $ServiceName; Start-Sleep -Seconds 2; $status = (Get-Service -Name $ServiceName).Status; if ($status -eq "Running") { Write-Host "Service restarted successfully" -ForegroundColor Green } else { Write-Host "WARNING: Service status is: $status" -ForegroundColor Yellow; exit 1 } } else { Write-Host "ERROR: Service not found" -ForegroundColor Red; exit 1 }`;
        commandType = 'powershell';
      } else if (os.includes('linux')) {
        // Linux: Restart service using systemctl
        restartCommand = `#!/bin/bash
SERVICE_NAME="uem-agent"
echo "Restarting UEM Agent service..."
if systemctl list-units --type=service | grep -q "^"$SERVICE_NAME""; then
  systemctl stop "$SERVICE_NAME" 2>/dev/null || true
  sleep 2
  systemctl start "$SERVICE_NAME"
  sleep 2
  SERVICE_STATUS=$(systemctl is-active "$SERVICE_NAME" 2>/dev/null || echo "unknown")
  if [ "$SERVICE_STATUS" = "active" ]; then
    echo "Service restarted successfully"
  else
    echo "ERROR: Service status is: $SERVICE_STATUS"
    exit 1
  fi
else
  echo "ERROR: Service not found"
  exit 1
fi`;
        commandType = 'bash';
      } else if (os.includes('macos') || os.includes('darwin')) {
        // macOS: Restart service using launchctl
        restartCommand = `#!/bin/bash
SERVICE_NAME="com.uem.agent"
echo "Restarting UEM Agent service..."
if launchctl list | grep -q "$SERVICE_NAME"; then
  launchctl stop "$SERVICE_NAME" 2>/dev/null || launchctl unload "/Library/LaunchDaemons/$SERVICE_NAME.plist" 2>/dev/null || true
  sleep 2
  launchctl load "/Library/LaunchDaemons/$SERVICE_NAME.plist" 2>/dev/null || launchctl start "$SERVICE_NAME" 2>/dev/null
  sleep 2
  if launchctl list | grep -q "$SERVICE_NAME"; then
    echo "Service restarted successfully"
  else
    echo "ERROR: Service may not have started"
    exit 1
  fi
else
  echo "ERROR: Service not found"
  exit 1
fi`;
        commandType = 'bash';
      } else {
        throw new Error(`Unsupported OS: ${os}`);
      }

      setRestartStatus(prev => ({ ...prev, [agent.device_id]: 'Sending restart command...' }));

      // Execute the restart command
      const result = await apiService.executeGoAgentCommand(agent.device_id, {
        command_type: commandType,
        command: restartCommand,
        timeout_seconds: 60, // 1 minute timeout for restart
        created_by: 'dashboard-service-restart'
      });

      setRestartStatus(prev => ({ ...prev, [agent.device_id]: 'Restart command sent. Waiting for completion...' }));
      setExecutionResult(`Restart command submitted successfully! Command ID: ${result.command_id}`);

      // Poll for command completion
      const deviceId = agent.device_id;
      const pollInterval = setInterval(async () => {
        try {
          const commands = await apiService.getGoAgentCommands(deviceId, { limit: 10 });
          const restartCmd = commands.items?.find(cmd => cmd.command_id === result.command_id);
          
          if (restartCmd) {
            if (restartCmd.status === 'completed') {
              clearInterval(pollInterval);
              setRestartStatus(prev => ({ ...prev, [deviceId]: 'Service restarted successfully!' }));
              setRestarting(prev => prev === deviceId ? null : prev);
              // Refresh agent status after restart
              setTimeout(() => {
                loadAgents();
                if (selectedAgent?.device_id === deviceId) {
                  loadAgentStatus();
                }
              }, 2000);
            } else if (restartCmd.status === 'failed') {
              clearInterval(pollInterval);
              setRestartStatus(prev => ({ ...prev, [deviceId]: 'Restart failed!' }));
              setRestarting(prev => prev === deviceId ? null : prev);
              setError(`Restart failed: ${restartCmd.result?.error_message || 'Unknown error'}`);
            } else if (restartCmd.status === 'running') {
              setRestartStatus(prev => ({ ...prev, [deviceId]: 'Restarting service...' }));
            }
          }
        } catch (err) {
          console.error('Error polling restart status:', err);
        }
      }, 3000); // Poll every 3 seconds

      // Clear interval after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setRestarting(prev => prev === deviceId ? null : prev);
      }, 120000);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restart service';
      setError(errorMessage);
      setRestartStatus(prev => ({ ...prev, [agent.device_id]: `Error: ${errorMessage}` }));
      setRestarting(null);
      console.error('Restart service error:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'online' || statusLower === 'active') {
      return <span className="status-badge online">Online</span>;
    } else if (statusLower === 'offline') {
      return <span className="status-badge offline">Offline</span>;
    } else if (statusLower === 'warning') {
      return <span className="status-badge warning">Warning</span>;
    }
    return <span className="status-badge unknown">{status}</span>;
  };

  const getCommandStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'completed') {
      return <span className="status-badge completed">Completed</span>;
    } else if (statusLower === 'pending') {
      return <span className="status-badge pending">Pending</span>;
    } else if (statusLower === 'executing') {
      return <span className="status-badge executing">Executing</span>;
    } else if (statusLower === 'failed') {
      return <span className="status-badge failed">Failed</span>;
    }
    return <span className="status-badge unknown">{status}</span>;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading && agents.length === 0) {
    return (
      <div className="go-agent-management">
        <div className="loading-spinner">{t('common.loading')} {t('goAgentManagement.goAgents')}...</div>
      </div>
    );
  }

  return (
    <div className="go-agent-management">
      {error && (
        <div className="error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {executionResult && (
        <div className="success-banner">
          <span>✓</span>
          <span>{executionResult}</span>
          <button onClick={() => setExecutionResult(null)}>×</button>
        </div>
      )}

      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'agents' ? 'active' : ''}`}
          onClick={() => setActiveTab('agents')}
        >
          {t('goAgentManagement.goAgents')} ({agents.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'patches' ? 'active' : ''}`}
          onClick={() => setActiveTab('patches')}
          disabled={!selectedAgent}
        >
          <ShieldAlert size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          {t('goAgentManagement.patches', 'Patches')}
        </button>
        <button
          className={`tab-button ${activeTab === 'customScripts' ? 'active' : ''}`}
          onClick={() => setActiveTab('customScripts')}
          disabled={!selectedAgent}
        >
          {t('goAgentManagement.runCustomScripts')}
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
          disabled={!selectedAgent}
        >
          {t('goAgentManagement.commandHistory')}
        </button>
        <button
          className={`tab-button ${activeTab === 'osquery' ? 'active' : ''}`}
          onClick={() => setActiveTab('osquery')}
          disabled={!selectedAgent}
        >
          <Database size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          Osquery
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'agents' && (
          <div className="agents-tab">
            <div className="agents-list">
              <table className="agents-table">
                <thead>
                  <tr>
                    <th>{t('goAgentManagement.deviceName')}</th>
                    <th>{t('goAgentManagement.os')}</th>
                    <th>{t('goAgentManagement.osCategory')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('goAgentManagement.lastSeen')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        {t('goAgentManagement.noAgentsFound', 'No Go agents found. Make sure agents are running and registered.')}
                      </td>
                    </tr>
                  ) : (
                    agents.map((agent) => (
                      <tr
                        key={agent.device_id}
                        className={selectedAgent?.device_id === agent.device_id ? 'selected' : ''}
                        onClick={() => {
                          setSelectedAgent(agent);
                          setActiveTab('customScripts');
                        }}
                      >
                        <td>
                          <div className="device-name">{agent.name || 'Unknown'}</div>
                          <div className="device-id">{agent.device_id.substring(0, 8)}...</div>
                        </td>
                        <td>
                          <span className="os-badge">{agent.os}</span>
                        </td>
                        <td>
                          <span className="os-badge">{getOSCategory(agent.os)}</span>
                        </td>
                        <td>
                          {(() => {
                            const isOnline = isLastSeenOnline(agent.last_seen);
                            return <span className={`status-badge ${isOnline ? 'online' : 'offline'}`}>{isOnline ? t('common.online') : t('common.offline')}</span>;
                          })()}
                        </td>
                        <td>{formatDate(agent.last_seen)}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button
                              className="icon-btn icon-btn-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAgent(agent);
                                setActiveTab('customScripts');
                              }}
                              title={t('goAgentManagement.select')}
                            >
                              <Play size={14} />
                            </button>
                            <button
                              className="icon-btn icon-btn-info"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScanInventory(agent);
                              }}
                              disabled={scanningDeviceId === agent.device_id}
                              title="Scan inventory"
                            >
                              {scanningDeviceId === agent.device_id ? (
                                <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Scan size={14} />
                              )}
                            </button>
                            <button
                              className="icon-btn icon-btn-info"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestartService(agent);
                              }}
                              disabled={restarting === agent.device_id}
                              title={t('goAgentManagement.restartService')}
                            >
                              <RefreshCw size={14} className={restarting === agent.device_id ? 'animate-spin' : ''} />
                            </button>
                            <button
                              className="icon-btn icon-btn-success"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateAgent(agent);
                              }}
                              disabled={updating === agent.device_id}
                              title={t('goAgentManagement.updateAgent')}
                            >
                              <Download size={14} className={updating === agent.device_id ? 'animate-pulse' : ''} />
                            </button>
                            <button
                              className="icon-btn icon-btn-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDevice(agent.device_id, agent.name || 'Unknown');
                              }}
                              disabled={deleting === agent.device_id}
                              title={t('goAgentManagement.delete')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          {restartStatus[agent.device_id] && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: '#0891b2' }}>
                              {restartStatus[agent.device_id]}
                            </div>
                          )}
                          {updateStatus[agent.device_id] && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: '#059669' }}>
                              {updateStatus[agent.device_id]}
                            </div>
                          )}
                          {scanMessage?.deviceId === agent.device_id && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: scanMessage.type === 'success' ? '#059669' : '#dc2626' }}>
                              {scanMessage.text}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {selectedAgent && (
              <div className="agent-details">
                <h3>{t('goAgentManagement.agentDetails', 'Agent Details')}</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>{t('goAgentManagement.deviceId', 'Device ID')}:</label>
                    <span>{selectedAgent.device_id}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.name', 'Name')}:</label>
                    <span>{selectedAgent.name || t('common.unknown', 'Unknown')}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('goAgentManagement.os')}:</label>
                    <span>{selectedAgent.os}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('goAgentManagement.osCategory')}:</label>
                    <span>{getOSCategory(selectedAgent.os)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.status')}:</label>
                    <span>{getStatusBadge(agentStatus?.status || selectedAgent.status)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('goAgentManagement.lastSeen')}:</label>
                    <span>{formatDate(agentStatus?.last_seen || selectedAgent.last_seen)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Patches tab */}
        {activeTab === 'patches' && (
          <div className="patches-tab">
            {!selectedAgent ? (
              <div className="empty-state">
                <p>{t('goAgentManagement.selectAgentForPatches', 'Select an agent from the Agents tab to view available patches.')}</p>
              </div>
            ) : patchReportsLoading ? (
              <div className="loading-spinner">{t('common.loading')} patch data...</div>
            ) : !patchReports || patchReports.length === 0 ? (
              <div className="empty-state">
                <p>{t('goAgentManagement.noPatchReports', 'No patch reports for this device. The agent may not have patch monitoring enabled, or no updates are available.')}</p>
              </div>
            ) : (
              <div className="patches-list">
                <h3>{t('goAgentManagement.availablePatches', 'Available Patches')} — {selectedAgent.name || selectedAgent.device_id}</h3>
                <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
                  {patchReports.length} {patchReports.length === 1 ? 'update' : 'updates'} reported by the agent (from device metadata).
                </p>
                <table className="patches-table">
                  <thead>
                    <tr>
                      <th>{t('goAgentManagement.packageName', 'Package / Update')}</th>
                      <th>{t('goAgentManagement.currentVersion', 'Current')}</th>
                      <th>{t('goAgentManagement.newVersion', 'New')}</th>
                      <th>{t('goAgentManagement.severity', 'Severity')}</th>
                      <th>{t('goAgentManagement.cveIds', 'CVE IDs')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patchReports.map((report, idx) => (
                      <tr key={`${report.package_name}-${idx}`}>
                        <td className="package-name">{report.package_name}</td>
                        <td>{report.current_version ?? '—'}</td>
                        <td>{report.new_version ?? '—'}</td>
                        <td>
                          {report.severity ? (
                            <span className={`severity-badge severity-${(report.severity || '').toLowerCase().replace(/\s+/g, '-')}`}>
                              {report.severity}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {report.cve_ids && report.cve_ids.length > 0 ? (
                            <span className="cve-list">{report.cve_ids.join(', ')}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-tab">
            {!selectedAgent ? (
              <div className="empty-state">
                <p>Please select an agent from the Agents tab to view command history.</p>
              </div>
            ) : (
              <div className="commands-list">
                <h3>Command History for: {selectedAgent.name || selectedAgent.device_id}</h3>
                <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
                  Showing only Agent Update and Service Restart commands
                </p>
                {(() => {
                  // Filter to only show Update Agent and Restart Service commands
                  const filteredCommands = commands.filter(cmd => 
                    cmd.created_by === 'dashboard-agent-update' || 
                    cmd.created_by === 'dashboard-service-restart'
                  );
                  
                  if (filteredCommands.length === 0) {
                    return (
                      <div className="empty-state">
                        <p>No update or restart commands found for this device.</p>
                      </div>
                    );
                  }
                  
                  return (
                    <table className="commands-table">
                      <thead>
                        <tr>
                          <th>Command ID</th>
                          <th>Type</th>
                          <th>Action</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Completed</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCommands.map((cmd) => {
                          const actionType = cmd.created_by === 'dashboard-agent-update' 
                            ? 'Update Agent' 
                            : cmd.created_by === 'dashboard-service-restart'
                            ? 'Restart Service'
                            : 'Unknown';
                          
                          return (
                            <tr key={cmd.command_id}>
                              <td className="command-id">{cmd.command_id.substring(0, 8)}...</td>
                              <td>
                                <span className="command-type-badge">{cmd.command_type}</span>
                              </td>
                              <td>
                                <span className="command-type-badge" style={{ 
                                  backgroundColor: cmd.created_by === 'dashboard-agent-update' ? '#059669' : '#0891b2' 
                                }}>
                                  {actionType}
                                </span>
                              </td>
                              <td>{getCommandStatusBadge(cmd.status)}</td>
                              <td>{formatDate(cmd.created_at)}</td>
                              <td>{formatDate(cmd.completed_at || '')}</td>
                              <td>
                                {cmd.status === 'completed' && cmd.result ? (
                                  <details className="result-details">
                                    <summary>► View Result</summary>
                                    <pre className="result-output">
                                      {typeof cmd.result === 'object' 
                                        ? cmd.result.output || JSON.stringify(cmd.result, null, 2)
                                        : cmd.result}
                                    </pre>
                                    {cmd.result.exit_code !== undefined && (
                                      <div className="exit-code">
                                        Exit Code: {cmd.result.exit_code}
                                      </div>
                                    )}
                                  </details>
                                ) : cmd.status === 'failed' ? (
                                  <span className="error-text">{cmd.error_message || 'Failed'}</span>
                                ) : (
                                  <span className="pending-text">Pending...</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === 'osquery' && (
          <div className="osquery-tab">
            {!selectedAgent ? (
              <div className="empty-state">
                <p>Select an agent to view Osquery snapshots.</p>
              </div>
            ) : osqueryLoading ? (
              <div className="loading-spinner">Loading Osquery data...</div>
            ) : osquerySnapshots.length === 0 ? (
              <div className="empty-state">
                <p>No Osquery snapshots for this device. Enable Osquery in Settings → Osquery and ensure the agent has osqueryi installed.</p>
              </div>
            ) : (
              <div className="osquery-snapshots">
                <h3>Osquery Snapshots — {selectedAgent.name || selectedAgent.device_id}</h3>
                <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
                  {osquerySnapshots.length} snapshot(s) from automated Osquery queries.
                </p>
                {(() => {
                  const byQuery = osquerySnapshots.reduce((acc: Record<string, any[]>, s) => {
                    const q = s.query_name || 'unknown';
                    if (!acc[q]) acc[q] = [];
                    acc[q].push(s);
                    return acc;
                  }, {});
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {Object.entries(byQuery).map(([queryName, snaps]) => (
                        <div key={queryName} className="card" style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600 }}>{queryName}</h4>
                          {snaps.slice(0, 3).map((s, i) => (
                            <details key={s.id || i} style={{ marginBottom: '8px' }}>
                              <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: '13px' }}>
                                {s.recorded_at ? new Date(s.recorded_at).toLocaleString() : '—'}
                              </summary>
                              <pre style={{ margin: '8px 0 0 0', padding: '12px', background: '#f9fafb', borderRadius: '6px', overflow: 'auto', fontSize: '12px' }}>
                                {typeof s.result_json === 'object' ? JSON.stringify(s.result_json, null, 2) : String(s.result_json ?? '')}
                              </pre>
                            </details>
                          ))}
                          {snaps.length > 3 && <p style={{ color: '#6b7280', fontSize: '12px', margin: '8px 0 0 0' }}>+{snaps.length - 3} more</p>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Run Custom Scripts Tab */}
        {activeTab === 'customScripts' && (
          <div className="custom-scripts-tab">
            {!selectedAgent ? (
              <div className="empty-state">
                <p>Please select an agent from the Agents tab to run custom scripts.</p>
              </div>
            ) : (
              <div className="command-form">
                <div className="form-section">
                  <h3>Run Custom Script on: {selectedAgent.name || selectedAgent.device_id}</h3>
                  <p style={{ color: '#6b7280', marginTop: '8px' }}>
                    Execute custom scripts (PowerShell, Bash, or CMD) on the selected device. Similar to Go Agent Upgrade functionality.
                  </p>
                </div>

                <div className="form-group">
                  <label>Script Type:</label>
                  <select
                    value={commandType}
                    onChange={(e) => setCommandType(e.target.value)}
                    disabled={executing}
                  >
                    <option value="powershell">PowerShell</option>
                    <option value="bash">Bash</option>
                    <option value="cmd">CMD</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Script Content:</label>
                  <textarea
                    value={commandText}
                    onChange={(e) => setCommandText(e.target.value)}
                    placeholder={`Enter ${commandType} script here...`}
                    rows={15}
                    disabled={executing}
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                  />
                  <small className="hint">
                    {commandType === 'powershell' 
                      ? 'Example: Get-ComputerInfo | Select-Object WindowsProductName, WindowsVersion'
                      : commandType === 'bash'
                      ? 'Example: echo "Hello World"; uname -a'
                      : 'Example: echo Hello World && systeminfo'}
                  </small>
                </div>

                <div className="form-group">
                  <label>Timeout (seconds):</label>
                  <input
                    type="number"
                    value={timeoutSeconds}
                    onChange={(e) => setTimeoutSeconds(parseInt(e.target.value) || 30)}
                    min={1}
                    max={3600}
                    disabled={executing}
                  />
                  <small className="hint">Maximum execution time for the script</small>
                </div>

                <div className="form-actions">
                  <button
                    className="execute-btn"
                    onClick={handleExecuteCommand}
                    disabled={executing || !commandText.trim()}
                  >
                    {executing ? 'Executing...' : 'Run Script'}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setCommandText('');
                      setError(null);
                      setExecutionResult(null);
                    }}
                    disabled={executing}
                  >
                    Clear
                  </button>
                </div>

                {executionResult && (
                  <div className="execution-result" style={{ 
                    marginTop: '20px', 
                    padding: '12px', 
                    backgroundColor: '#f0fdf4', 
                    border: '1px solid #86efac',
                    borderRadius: '6px'
                  }}>
                    <strong>✓ {executionResult}</strong>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default GoAgentManagement;

